import React, { useEffect, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { adminPaymentService, AdminPaymentDetail } from '@/services/adminPaymentService'
import { RefundModal } from '@/components/admin/RefundModal'

/**
 * AdminPaymentDetailPage — M5 admin refund entry point.
 *
 * Route: /admin/payments/:id
 */
export const AdminPaymentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [detail, setDetail] = useState<AdminPaymentDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refundOpen, setRefundOpen] = useState(false)

  const refresh = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const d = await adminPaymentService.getDetail(id)
      setDetail(d)
    } catch (e) {
      setError((e as Error).message || 'Gagal memuat payment')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void refresh()
  }, [refresh])

  if (loading) return <div className="p-6">Loading...</div>
  if (error || !detail)
    return (
      <div className="p-6 text-red-600" data-testid="admin-payment-error">
        {error || 'Not found'}
      </div>
    )

  const canRefund = detail.status === 'paid' && detail.refundStatus !== 'completed'

  return (
    <div>
      <PageHeader title={`Payment ${detail.externalId}`} subtitle={detail.paymentType} />
      <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5 space-y-2">
        <p>
          <strong>ID:</strong> {detail.id}
        </p>
        <p>
          <strong>Status:</strong> {detail.status}
        </p>
        <p>
          <strong>Refund Status:</strong> {detail.refundStatus}
        </p>
        <p>
          <strong>Amount:</strong> Rp {detail.amount.toLocaleString('id-ID')}
        </p>
        <p>
          <strong>Fee:</strong> Rp {detail.fee.toLocaleString('id-ID')}
        </p>
        <p>
          <strong>Enrollment:</strong> {detail.enrollmentId || '—'}
        </p>
        <div className="pt-3">
          <button
            type="button"
            onClick={() => setRefundOpen(true)}
            disabled={!canRefund}
            className="px-4 py-2 bg-red-600 text-white rounded disabled:opacity-50"
            data-testid="issue-refund-btn"
          >
            Issue Refund
          </button>
        </div>
      </div>
      <RefundModal
        isOpen={refundOpen}
        onClose={() => setRefundOpen(false)}
        paymentId={detail.id}
        onSuccess={refresh}
      />
    </div>
  )
}

export default AdminPaymentDetailPage
